import type { Cursus } from '../lib/types'

/* ===========================================================================
 *  Slim én veilig met AI
 *
 *  LEES DIT VOORDAT JE IETS WIJZIGT
 *  ---------------------------------
 *
 *  Deze cursus is geschreven door een AI. Dat is niet ironisch bedoeld en het
 *  is ook geen bezwaar -- het is precies de reden dat dit bestand er zo
 *  uitziet als het eruitziet: alles staat in gewone tekst, op één plek, zodat
 *  wie een fout vindt hem kan wegnemen zonder een programmeur.
 *
 *  Drie dingen om in de gaten te houden.
 *
 *  1. De juridische stukken kloppen voor zover ze na te kijken waren, maar
 *     wetgeving schuift. In de tijdlijn van module 1 staat per datum of hij
 *     vaststaat of nog in beweging is. Wat daar op `vast: false` staat, hoort
 *     nagekeken te worden voordat iemand er iets op baseert.
 *
 *  2. Module 5 bevat huisregels die HIER zijn voorgesteld en die niet door
 *     het management zijn vastgesteld. Ze staan er met een merkteken
 *     ('concept'), en dat merkteken hoort er te blijven tot iemand met de
 *     bevoegdheid erover heeft besloten. Een voorstel dat eruitziet als een
 *     regel is erger dan geen regel: mensen gaan zich eraan houden, en
 *     niemand heeft hem ooit goedgekeurd.
 *
 *  3. Cursisten kunnen hier zelf iets over zeggen. Onder elke module staat een
 *     knop die er een melding van maakt in het dashboard (migratie 0155). Wat
 *     daaruit komt hoort hier terecht te komen -- dat is de hele reden dat die
 *     knop er is.
 *
 *  Versie 2 -- ingekort
 *  --------------------
 *
 *  Casper: "Daarnaast moet het niet al te lang zijn."
 *
 *  Van zeven modules naar vijf, en van 25 naar 15 minuten. Wat eruit ging is
 *  de herhaling: elke module had twee tot drie alinea's die hetzelfde zeiden
 *  met andere woorden. En "wanneer moet je zeggen dat het AI is" was een eigen
 *  module terwijl het bij module 4 hoort -- dat gaat allebei over wat JIJ moet
 *  doen.
 *
 *  Wat er NIET uit ging: de vier werkvormen, de vijf filmpjes en geen enkele
 *  regel die ergens over gaat. De toets ging van tien naar acht vragen.
 *
 *  Het versienummer ging daarom van 1 naar 2. Dat is geen formaliteit: wie
 *  versie 1 had gehaald, moet deze opnieuw doen. Zie migratie 0147.
 * =========================================================================== */

export const AI_GEBRUIKEN: Cursus = {
  id: 'ai-gebruiken',
  versie: 2,
  titel: 'Slim én veilig met AI',
  ondertitel: 'ChatGPT, Claude en de rest — wat moet, wat mag, en wat echt niet',
  belofte:
    'Na deze cursus weet je wat je een chatbot wél en niet mag vertellen, '
    + 'waarom hij soms met droge ogen iets verzint, en waar jij zelf '
    + 'verantwoordelijk voor blijft.',
  duurMinuten: 15,
  accent: 'geel',
  slaagpercentage: 80,

  modules: [
    /* ================================================================ *
     *  1
     * ================================================================ */
    {
      id: 'm1',
      titel: 'Waarom dit geen extra klusje is',
      kop: 'AI zit al in je werk. De vraag is alleen of je weet wat je ermee doet.',
      blokken: [
        {
          soort: 'film',
          titel: 'Het pand uit',
          ondertitel: 'Waar je woorden heen gaan.',
          scenes: [
            {
              shot: 'naar-buiten',
              duur: 6500,
              zeg:
                'Je typt een vakvraag in. Prima. Alleen blijft hij niet in de '
                + 'kantine — hij gaat naar een server van een ander bedrijf, '
                + 'meestal buiten Europa. En wat er per ongeluk achteraan komt, '
                + 'gaat mee.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Je gebruikt waarschijnlijk al AI, ook als je dat woord er zelf niet op plakt. Trucky beantwoordt vragen op onze website, de factuurlezer haalt bedragen van een papiertje, en in je eigen broekzak zit ChatGPT of Claude. Deze cursus is er niet om je dat af te nemen — hij is er zodat je het kunt blijven doen zonder dat er een keer iets misgaat waar we niet meer onderuit komen.',
            'Want er is één verschil met de zoekmachine waar je dit soort dingen vroeger in typte: alles wat je aan een chatbot vertelt, verlaat het bedrijf. En er staat sinds kort ook iets in de wet. In de Europese AI-verordening staat artikel 4, over AI-geletterdheid: een werkgever moet ervoor zorgen dat de mensen die met AI werken genoeg begrijpen van wat ze doen. Niet programmeren — begrijpen.',
          ],
        },
        {
          soort: 'tijdlijn',
          punten: [
            {
              wanneer: '1 augustus 2024',
              wat: 'De AI-verordening treedt in werking',
              toelichting:
                'Verordening (EU) 2024/1689. Vanaf dat moment loopt de klok; '
                + 'de verplichtingen gaan gefaseerd gelden.',
              vast: true,
            },
            {
              wanneer: '2 februari 2025',
              wat: 'AI-geletterdheid en de verboden toepassingen gelden',
              toelichting:
                'Artikel 4 (iedereen die met AI werkt moet er genoeg van '
                + 'begrijpen) en artikel 5 (wat sowieso niet mag, zoals '
                + 'emotieherkenning op de werkvloer). Dit is het artikel waar '
                + 'deze cursus onder valt.',
              vast: true,
            },
            {
              wanneer: '2 augustus 2026',
              wat: 'De transparantieplicht en het grootste deel van de rest',
              toelichting:
                'Onder meer: mensen moeten weten dat ze met een AI praten, en '
                + 'door AI gemaakt of bewerkt beeld en geluid moet als zodanig '
                + 'herkenbaar zijn. Let op: er zijn voorstellen geweest om '
                + 'onderdelen van deze fase uit te stellen. Kijk de stand na '
                + 'voordat je hier iets op baseert.',
              vast: false,
            },
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Wat je in een chatbot typt, verlaat het bedrijf.',
            'AI-geletterdheid is sinds februari 2025 geen goed voornemen maar een verplichting voor de werkgever.',
            'Deze cursus gaat over begrijpen wat je doet, niet over techniek.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  2
     * ================================================================ */
    {
      id: 'm2',
      titel: 'Wat zo’n ding eigenlijk doet',
      kop: 'Hij weet niets. Hij voorspelt. Dat verschil verklaart bijna alle ongelukken.',
      blokken: [
        {
          soort: 'film',
          titel: 'Hij weet het niet, hij gokt',
          ondertitel: 'Hoe een verzinsel eruitziet.',
          scenes: [
            {
              shot: 'voorspellen',
              duur: 6000,
              zeg:
                'Een taalmodel zoekt niets op. Het kiest het woord dat het beste '
                + 'klinkt na wat er staat. De zelfverzekerdste kandidaat wint — '
                + 'niet de juiste.',
            },
            {
              shot: 'verzinsel',
              duur: 5500,
              zeg:
                'En als er een nummer bij moet, verzint hij dat erbij. Een norm '
                + 'die nergens bestaat, met een stempel erop. Hoe officiëler het '
                + 'eruitziet, hoe minder mensen het nakijken.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Er is geen moment waarop het ding iets opzoekt, nakijkt of twijfelt. Het maakt de zin af die het meest waarschijnlijk klinkt. Is de waarheid toevallig ook het meest waarschijnlijke — en dat is ze vaak — dan krijg je een goed antwoord. Zo niet, dan krijg je een antwoord dat er precies zo overtuigd uitziet.',
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Een chatbot klinkt bij een fout precies zo zeker als bij iets wat '
            + 'klopt. Er zit geen twijfel in de stem. Jij bent de enige die kan '
            + 'zien dat er iets niet deugt.',
        },
        {
          soort: 'spot',
          inleiding:
            'Iemand vroeg een chatbot hoe vaak een borstelunit onderhouden moet '
            + 'worden. Dit kwam eruit. Tik de stukken aan waarvan jij denkt: dat '
            + 'zou ik nooit zomaar overnemen.',
          stukken: [
            {
              tekst:
                'Voor een roterende borstelunit in een truckwash geldt dat de '
                + 'lagers regelmatig gecontroleerd moeten worden op speling en '
                + 'geluid. ',
              fout: false,
              uitleg: 'Redelijk algemeen advies. Hier is niets mis mee.',
            },
            {
              tekst:
                'Volgens NEN-norm 4187-3 moet dit minimaal elke 250 draaiuren '
                + 'gebeuren. ',
              fout: true,
              uitleg:
                'Een normnummer met een precies getal erachter is het klassieke '
                + 'verzinsel. Het klinkt controleerbaar, en juist dáárom gelooft '
                + 'iedereen het. Een norm die je niet zelf hebt opgezocht, '
                + 'bestaat niet.',
            },
            {
              tekst:
                'Daarnaast is het verstandig de borstelharen te controleren op '
                + 'slijtage en vastzittend vuil, omdat versleten borstels krassen '
                + 'kunnen veroorzaken op de lak. ',
              fout: false,
              uitleg: 'Klopt, en het is te controleren door er gewoon naar te kijken.',
            },
            {
              tekst:
                'De fabrikant schrijft doorgaans een vervangingstermijn van 18 '
                + 'maanden voor. ',
              fout: true,
              uitleg:
                '"De fabrikant" — welke? Het model weet niet welke installatie wij '
                + 'hebben staan. Dit is een gemiddelde van alles wat het ooit '
                + 'gelezen heeft, gepresenteerd als een voorschrift dat voor ons '
                + 'zou gelden.',
            },
            {
              tekst:
                'Leg de bevindingen vast, zodat je bij een storing kunt terugzien '
                + 'wat er eerder is opgevallen.',
              fout: false,
              uitleg: 'Goed advies, en het kost niets om het te volgen.',
            },
          ],
          naderhand:
            'Zie je het patroon? Het klopt precies zolang het algemeen blijft. '
            + 'Zodra er een nummer, een norm of een merk in komt, is het gegokt. '
            + 'En dat is nu net het stuk dat je zou willen overnemen.',
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Het model voorspelt tekst, het zoekt niets op.',
            'Hoe concreter het antwoord — getallen, normen, merken, wetsartikelen — hoe groter de kans dat het verzonnen is.',
            'De toon zegt niets over de juistheid.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  3
     * ================================================================ */
    {
      id: 'm3',
      titel: 'Wat gaat er niet in',
      kop: 'De belangrijkste module. Eén keer fout is hier al te veel.',
      blokken: [
        {
          soort: 'film',
          titel: 'De poort',
          ondertitel: 'Wat er wel en niet door mag.',
          scenes: [
            {
              shot: 'poort',
              duur: 6500,
              zeg:
                'Zet er een poort voor. Gereedschap en vakvragen mogen erdoor. '
                + 'Alles wat naar een persoon wijst — een naam, een kenteken, een '
                + 'BSN — komt er niet langs, hoe hard het ook probeert.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Wat er met je tekst gebeurt verschilt per product en per instelling, en het verandert. Bij zakelijke abonnementen staat het meetrainen op jouw gesprekken doorgaans uit; bij gratis accounts vaak niet. Die afweging hoef jij niet te maken en kún je ook niet maken, want je ziet die instellingen niet. Daarom is de regel simpeler dan de werkelijkheid: ga ervan uit dat wat je intypt blijft bestaan en door anderen gelezen kan worden.',
            'Voor persoonsgegevens is het bovendien een kwestie van de AVG. Een persoonsgegeven is alles wat te herleiden is tot een persoon: een naam, een mailadres, een telefoonnummer, een pasfoto, vaak ook een kenteken. En bijzondere gegevens — gezondheid, ziekmeldingen, godsdienst, vakbond — liggen nog gevoeliger.',
          ],
        },
        {
          soort: 'sorteer',
          vraag: 'Mag dit in een gewone chatbot?',
          linksLabel: 'Niet doen',
          rechtsLabel: 'Prima',
          kaarten: [
            {
              id: 's1',
              tekst: 'Hoe krijg ik hardnekkige insectenresten van een voorruit?',
              rechts: true,
              uitleg:
                'Een vakvraag zonder iets van iemand erin. Precies waar zo’n '
                + 'ding goed in is.',
            },
            {
              id: 's2',
              tekst: 'Een klacht van een klant, met bedrijfsnaam en telefoonnummer erin, om een nette reactie te laten schrijven.',
              rechts: false,
              uitleg:
                'De vraag mag, de gegevens niet. Haal de naam, het nummer en de '
                + 'plaats eruit en vraag om een reactie op de situatie. Het '
                + 'antwoord wordt er niet slechter van.',
            },
            {
              id: 's3',
              tekst: 'Je eigen BSN, om te laten uitleggen wat er op een formulier moet.',
              rechts: false,
              uitleg:
                'Ook je eigen gegevens niet. Het formulier is de vraag; je BSN '
                + 'voegt niets toe aan het antwoord en alles aan het risico.',
            },
            {
              id: 's4',
              tekst: 'De tekst van een vacature die we willen plaatsen, laten aanscherpen.',
              rechts: true,
              uitleg: 'Openbare tekst die juist bedoeld is om gelezen te worden.',
            },
            {
              id: 's5',
              tekst: 'Een foto van een vrachtbrief, om de gegevens eruit te laten halen.',
              rechts: false,
              uitleg:
                'Daar staat een klant op, een chauffeur, een adres en een lading. '
                + 'Hiervoor hebben we onze eigen lezer in het dashboard — die '
                + 'houdt het binnen.',
            },
            {
              id: 's6',
              tekst: 'Een salarisstrook, om te laten uitleggen wat de posten betekenen.',
              rechts: false,
              uitleg:
                'Loongegevens van een herleidbare persoon. Wil je weten wat een '
                + 'post betekent, vraag dan naar de post — niet naar de strook.',
            },
            {
              id: 's7',
              tekst: 'Het wachtwoord van de kassa, om te laten controleren of het sterk genoeg is.',
              rechts: false,
              uitleg:
                'Een wachtwoord dat je ergens intypt waar het niet hoort, is vanaf '
                + 'dat moment geen wachtwoord meer. Verander het ook als het per '
                + 'ongeluk is gebeurd.',
            },
            {
              id: 's8',
              tekst: 'Een sollicitatiebrief laten beoordelen en er een rangorde van maken.',
              rechts: false,
              uitleg:
                'Twee problemen tegelijk: het zijn persoonsgegevens, en het gaat '
                + 'over een besluit dat gevolgen heeft voor iemand. Daar mag een '
                + 'machine niet alleen over gaan. Meer daarover in module 4.',
            },
            {
              id: 's9',
              tekst: 'Een formule voor Excel vragen om uren op te tellen.',
              rechts: true,
              uitleg: 'Gereedschap vragen, geen gegevens delen. Prima.',
            },
            {
              id: 's10',
              tekst: 'De ziekmelding van een collega, met naam, om een briefje aan het team te laten opstellen.',
              rechts: false,
              uitleg:
                'Gezondheidsgegevens zijn bijzondere persoonsgegevens. Die horen '
                + 'nergens buiten het bedrijf, en eerlijk gezegd ook niet in een '
                + 'briefje aan het team.',
            },
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Is het toch gebeurd? Meld het meteen bij je leidinggevende of bij '
            + 'kantoor. Niet omdat er straf op staat, maar omdat een datalek '
            + 'binnen 72 uur gemeld moet kunnen worden — en die klok begint te '
            + 'lopen op het moment dat het bedrijf ervan weet, niet wanneer het '
            + 'uitkomt.',
        },
        {
          soort: 'kern',
          titel: 'De vuistregel',
          punten: [
            'Geen namen, geen adressen, geen kentekens, geen foto’s van mensen of documenten.',
            'Geen wachtwoorden, geen sleutels, geen toegangscodes.',
            'Geen gezondheid, geen loon, geen dossier — ook niet van jezelf.',
            'Twijfel je? Haal het eruit. Het antwoord wordt er zelden minder door.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  4
     * ================================================================ */
    {
      id: 'm4',
      titel: 'Jij blijft aan de knoppen',
      kop: 'De AI schrijft. Jij verstuurt — en jij zegt wanneer het AI was.',
      blokken: [
        {
          soort: 'film',
          titel: 'Jij tekent',
          ondertitel: 'Wiens naam eronder staat.',
          scenes: [
            {
              shot: 'handtekening',
              duur: 6500,
              zeg:
                'De machine ratelt de brief eruit. Mooi. Maar dat bedrag heeft ze '
                + 'geraden, want jij hebt het niet opgegeven — en jouw naam staat '
                + 'eronder, niet die van haar.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Er bestaat geen situatie waarin "dat had de AI gezegd" een antwoord is. Wat jij verstuurt is van jou. Dat geeft je een simpele toets voordat je iets overneemt: weet ik waar dit getal vandaan komt, en kan ik dit uitleggen als iemand ernaar vraagt?',
          ],
        },
        {
          soort: 'keuze',
          situatie:
            'Je hebt een chatbot een mail laten opstellen aan een klant over schade '
            + 'aan een spatbord. Er staat een bedrag in: € 340 exclusief btw. Je hebt '
            + 'dat bedrag zelf niet genoemd.',
          vraag: 'Wat doe je?',
          opties: [
            {
              tekst: 'Versturen — het klinkt aannemelijk voor zo’n reparatie.',
              goed: false,
              uitleg:
                'Aannemelijk is precies waar het model op mikt. Het heeft een bedrag '
                + 'gekozen dat goed staat in de zin. Ga je dit versturen, dan heeft '
                + 'de klant een prijsopgave van niemand.',
            },
            {
              tekst: 'Het bedrag eruit halen en er zelf het juiste bedrag in zetten.',
              goed: true,
              uitleg:
                'Precies. Alles wat het model invult en jij niet hebt aangeleverd, '
                + 'is geraden. Haal het eruit of vervang het door iets wat je kunt '
                + 'onderbouwen.',
            },
            {
              tekst: 'Vragen of het bedrag klopt.',
              goed: false,
              uitleg:
                'Dat levert een nieuw antwoord op dat net zo goed geraden is. Een '
                + 'model kan zijn eigen verzinsels niet nakijken — het heeft geen '
                + 'plek om te kijken.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Bij besluiten over mensen is dit niet alleen verstandig maar geregeld. In de AVG staat dat iemand niet onderworpen mag worden aan een besluit dat uitsluitend op een geautomatiseerde verwerking berust en dat hem in aanmerkelijke mate treft. In gewoon Nederlands: een machine mag niet in haar eentje beslissen over aannemen, afwijzen of beoordelen. Er moet een mens zijn die er werkelijk naar kijkt en die ook tot een ander oordeel kan komen — iemand die alleen op "akkoord" drukt, is dat niet.',
          ],
        },
        {
          soort: 'keuze',
          situatie:
            'Er zijn elf sollicitaties binnen voor twee plekken. Iemand stelt voor om '
            + 'ze door een chatbot te laten rangschikken, zodat jullie alleen de '
            + 'bovenste vier hoeven te lezen.',
          vraag: 'Wat is hier mis mee?',
          opties: [
            {
              tekst: 'Niets, zolang een mens de bovenste vier nog leest.',
              goed: false,
              uitleg:
                'De zeven die afvallen zijn dan al afgevallen door de machine, en '
                + 'niemand heeft ze gezien. Dat is precies het besluit dat niet '
                + 'uitsluitend geautomatiseerd mag zijn.',
            },
            {
              tekst: 'Twee dingen: het zijn persoonsgegevens die het bedrijf verlaten, én de afwijzing komt feitelijk van de machine.',
              goed: true,
              uitleg:
                'Allebei, en allebei zwaar genoeg op zichzelf. Wil je hulp bij '
                + 'sollicitaties, gebruik het dan voor iets zonder gegevens: een '
                + 'lijst met vragen voor het gesprek bijvoorbeeld.',
            },
            {
              tekst: 'Alleen dat de brieven het bedrijf verlaten.',
              goed: false,
              uitleg:
                'Dat is één van de twee. Zelfs met een systeem dat de gegevens '
                + 'binnenhoudt, blijft staan dat een mens de afweging moet maken.',
            },
          ],
        },
        {
          soort: 'film',
          titel: 'Zeg het gewoon',
          ondertitel: 'De transparantieplicht, in het kort.',
          scenes: [
            {
              shot: 'masker',
              duur: 6000,
              zeg:
                'Praat iemand met een AI, dan hoort hij dat te weten. Meer vraagt de '
                + 'regel niet. Geen masker nodig — het is geen schande, het is alleen '
                + 'geen mens.',
            },
          ],
        },
        {
          soort: 'vergelijk',
          nietTitel: 'Hier moet erbij dat het AI is',
          niet: [
            'Een chatbot op onze site die vragen van bezoekers beantwoordt.',
            'Bewerkt of nagemaakt beeld dat echt lijkt.',
            'Een ingesproken bericht met een nagemaakte stem.',
            'Een "klantreactie" die niet van een klant komt.',
          ],
          welTitel: 'Hier hoeft dat niet',
          wel: [
            'Een mail die jij hebt opgesteld, nagelezen en zelf ondertekent.',
            'Een samenvatting van een overleg die jij hebt gecontroleerd.',
            'Een Excel-formule of een stuk code.',
            'Een eerste opzet die daarna door een mens wordt vastgesteld.',
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Wat jij verstuurt is van jou, ongeacht wie het heeft getypt.',
            'Elk getal en elke bron die jij niet hebt aangeleverd, is geraden.',
            'Besluiten met gevolgen voor mensen: nooit door de machine alleen.',
            'Praat iemand met een AI, of is beeld nagemaakt? Zeg het.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  5
     * ================================================================ */
    {
      id: 'm5',
      titel: 'Beter vragen, en de huisregels',
      kop: 'Hetzelfde gereedschap, de helft van het gedoe — en alles op één kaartje.',
      blokken: [
        {
          soort: 'tekst',
          alineas: [
            'De meeste teleurstellende antwoorden komen door teleurstellende vragen. Een chatbot kan niet raden in welke situatie jij zit of voor wie de tekst is, dus vult hij dat zelf in. Bouw de vraag hieronder op en let vooral op het laatste stuk: vragen wat het ding níét weet is het enige wat een beetje tegen verzinsels helpt.',
          ],
        },
        {
          soort: 'prompt',
          opdracht:
            'Je wilt een korte instructie voor nieuwe medewerkers over het aanmaken '
            + 'van reinigingsmiddel.',
          kaalAntwoord:
            'Bij het aanmaken van reinigingsmiddelen is het belangrijk om zorgvuldig '
            + 'te werk te gaan. Lees altijd het etiket, draag geschikte '
            + 'beschermingsmiddelen en volg de voorschriften van de fabrikant. Meng '
            + 'nooit verschillende producten met elkaar…',
          volAntwoord:
            'Aanmaken van ontvetter — voor je eerste week\n\n'
            + '1. Trek nitril handschoenen en een spatbril aan. Latex is hier niet genoeg.\n'
            + '2. Vul de emmer eerst met water, daarna pas het middel erbij. Andersom spat het.\n'
            + '3. Houd de verhouding aan die op het etiket staat — meer is niet schoner.\n'
            + '4. Meng nooit twee middelen.\n'
            + '5. Weet je het niet zeker? Vraag het, ook al voelt dat als een domme vraag.\n\n'
            + 'Wat ik niet weet: welke middelen jullie precies gebruiken en welke '
            + 'verhoudingen daarbij horen. Vul dat in vanaf het etiket of het '
            + 'veiligheidsinformatieblad.',
          stukken: [
            {
              id: 'p1',
              label: 'Zeg wat het moet worden',
              tekst: 'Schrijf een korte werkinstructie',
              waarom:
                'Zonder dit krijg je een verhandeling. Een instructie, een mail en '
                + 'een samenvatting zijn drie verschillende dingen.',
              nodig: true,
            },
            {
              id: 'p2',
              label: 'Zeg voor wie',
              tekst: 'voor iemand die hier zijn eerste week werkt',
              waarom:
                'Dit bepaalt het woordgebruik en wat er wel en niet uitgelegd moet '
                + 'worden. Het scheelt meer dan alle andere stukken bij elkaar.',
              nodig: true,
            },
            {
              id: 'p3',
              label: 'Geef de situatie',
              tekst: 'over het aanmaken van ontvetter in een truckwash',
              waarom:
                'Zonder context vult het model een gemiddelde werkplek in — en dat '
                + 'is nergens onze werkplek.',
              nodig: true,
            },
            {
              id: 'p4',
              label: 'Zeg hoe het eruit moet zien',
              tekst: 'als genummerde stappen, maximaal acht regels',
              waarom:
                'Vorm en lengte vraagt hij anders niet, en dan krijg je vier '
                + 'alinea’s waar vijf regels moesten staan.',
              nodig: true,
            },
            {
              id: 'p5',
              label: 'Vraag wat hij niet weet',
              tekst:
                'zet er onderaan bij welke gegevens je niet hebt en dus niet hebt ingevuld',
              waarom:
                'Het belangrijkste stuk. Hiermee dwing je hem de gaten te benoemen '
                + 'in plaats van ze op te vullen met iets aannemelijks. Het is geen '
                + 'garantie, maar het scheelt.',
              nodig: true,
            },
            {
              id: 'p6',
              label: 'Wees beleefd',
              tekst: 'alsjeblieft',
              waarom:
                'Mag, kost niets, en helpt niet. Het model wordt niet behulpzamer '
                + 'van dankjewel. Dit stuk staat erbij om te laten zien dat langer '
                + 'niet hetzelfde is als beter.',
              nodig: false,
            },
          ],
        },
        {
          soort: 'concept',
          tekst:
            'De huisregels hieronder zijn een voorstel. Ze zijn opgesteld bij het '
            + 'bouwen van deze cursus en nog niet door de directie vastgesteld. '
            + 'Zolang dit blok er staat, zijn het aanbevelingen en geen '
            + 'bedrijfsregels. Wie ze vaststelt, haalt dit blok weg.',
        },
        {
          soort: 'kern',
          titel: 'Wat altijd mag',
          punten: [
            'Vakvragen stellen die niets over een persoon of een klant zeggen.',
            'Je eigen tekst laten nakijken op toon, spelling en lengte.',
            'Een eerste opzet laten maken die jij daarna afmaakt.',
            'Hulp vragen bij gereedschap: formules, sjablonen, checklists.',
          ],
        },
        {
          soort: 'kern',
          titel: 'Wat nooit gaat',
          punten: [
            'Persoonsgegevens: namen, adressen, mailadressen, telefoonnummers, kentekens, foto’s.',
            'Gezondheid, ziekte, loon, dossier — van een ander of van jezelf.',
            'Wachtwoorden, codes, sleutels.',
            'Klantdocumenten: vrachtbrieven, facturen, contracten, camerabeelden.',
            'Een besluit over een mens door de machine laten nemen.',
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Weet je het niet zeker? Vraag het aan je leidinggevende of aan kantoor '
            + 'voordat je het intypt. Daarna kan het niet meer terug.',
        },
      ],
    },
  ],

  /* ================================================================== *
   *  De toets
   *
   *  Acht situaties en geen definities. "Wat is een taalmodel" toetst of
   *  iemand de module heeft gelezen; "de klant stuurt een vrachtbrief, wat
   *  doe je" toetst of hij het snapt. Alleen het tweede voorkomt iets.
   * ================================================================== */
  toets: [
    {
      id: 't1',
      situatie:
        'Een klant belt met een klacht en stuurt een mail na met zijn naam, '
        + 'bedrijfsnaam en telefoonnummer erin. Je wilt hulp bij een nette reactie.',
      vraag: 'Wat doe je?',
      opties: [
        'De mail plakken in de chatbot en om een reactie vragen',
        'De situatie beschrijven zonder naam, bedrijf en nummer, en daarop een reactie vragen',
        'De mail plakken maar erbij zetten dat de gegevens vertrouwelijk zijn',
        'Niets — hulp vragen mag hier niet',
      ],
      goed: 1,
      uitleg:
        'De vraag mag, de gegevens niet. En "dit is vertrouwelijk" erbij zetten '
        + 'verandert niets aan waar de tekst heen gaat.',
    },
    {
      id: 't2',
      vraag: 'Waarom klinkt een verzonnen antwoord net zo overtuigend als een goed antwoord?',
      opties: [
        'Omdat het model liegt als het het niet weet',
        'Omdat het model tekst voorspelt en niet weet wanneer het iets niet weet',
        'Omdat de instellingen verkeerd staan',
        'Omdat het antwoord uit een verouderde bron komt',
      ],
      goed: 1,
      uitleg:
        'Er zit geen moment in waarop het ding merkt dat het gokt. Het maakt de '
        + 'meest waarschijnlijke zin af — of die nu klopt of niet.',
    },
    {
      id: 't3',
      situatie:
        'In een door AI opgestelde onderhoudsinstructie staat: "conform NEN 4187-3 '
        + 'elke 250 draaiuren".',
      vraag: 'Wat is hier het meest waarschijnlijk aan de hand?',
      opties: [
        'Het klopt; NEN-normen zijn openbaar en goed vindbaar',
        'Het normnummer en het getal zijn waarschijnlijk verzonnen',
        'Het klopt als de fabrikant Duits is',
        'Het is een schrijffout in het nummer',
      ],
      goed: 1,
      uitleg:
        'Normnummers met precieze getallen zijn het klassieke verzinsel: ze zien '
        + 'eruit alsof ze te controleren zijn, en juist daarom controleert niemand ze.',
    },
    {
      id: 't4',
      situatie: 'Je hebt per ongeluk een salarisstrook in een chatbot geplakt.',
      vraag: 'Wat doe je?',
      opties: [
        'Het gesprek verwijderen en verder niets — dan is het weg',
        'Het diezelfde dag melden bij je leidinggevende of bij kantoor',
        'Wachten of er iets gebeurt',
        'Het melden bij de volgende functioneringsronde',
      ],
      goed: 1,
      uitleg:
        'Verwijderen haalt het niet van de server van de leverancier. En de klok '
        + 'voor een eventuele melding begint te lopen zodra het bedrijf ervan weet.',
    },
    {
      id: 't5',
      vraag:
        'Een collega wil elf sollicitatiebrieven door een chatbot laten rangschikken '
        + 'zodat jullie alleen de bovenste vier lezen. Wat klopt?',
      opties: [
        'Dat mag, want een mens leest de bovenste vier',
        'Dat mag als de brieven geanonimiseerd worden',
        'Dat mag niet: de zeven afgevallen kandidaten zijn dan door de machine afgewezen',
        'Dat mag alleen bij meer dan twintig brieven',
      ],
      goed: 2,
      uitleg:
        'Een besluit met aanmerkelijke gevolgen mag niet uitsluitend '
        + 'geautomatiseerd tot stand komen. Wie afvalt zonder dat iemand hem heeft '
        + 'gezien, is door de machine afgewezen.',
    },
    {
      id: 't6',
      vraag: 'Waar moet volgens de transparantieregels bij vermeld worden dat het AI is?',
      opties: [
        'Bij elke tekst die met AI-hulp is geschreven',
        'Bij een chatbot waar iemand mee praat, en bij nagemaakt of bewerkt beeld en geluid dat echt lijkt',
        'Alleen bij teksten voor klanten',
        'Nergens, zolang je het zelf nakijkt',
      ],
      goed: 1,
      uitleg:
        'Het gaat erom dat niemand voor de gek wordt gehouden over wat hij voor zich '
        + 'heeft. Een mail die jij hebt gecontroleerd en ondertekend, is jouw mail.',
    },
    {
      id: 't7',
      situatie:
        'De chatbot heeft een mail voor je opgesteld en er staat een bedrag in dat '
        + 'jij niet hebt genoemd.',
      vraag: 'Wat doe je?',
      opties: [
        'Versturen als het aannemelijk is',
        'Vragen of het bedrag klopt en het antwoord aanhouden',
        'Het bedrag eruit halen en zelf het juiste invullen',
        'Er "circa" voor zetten',
      ],
      goed: 2,
      uitleg:
        'Alles wat het model invult en jij niet hebt aangeleverd, is geraden. En '
        + 'navragen levert een tweede gok op, geen controle.',
    },
    {
      id: 't8',
      vraag: 'Welke van deze vier mag wél gewoon in een chatbot?',
      opties: [
        'Een foto van een vrachtbrief om de gegevens eruit te halen',
        'Een Excel-formule vragen om gewerkte uren op te tellen',
        'Een ziekmelding van een collega om een teambericht te laten schrijven',
        'Het kassawachtwoord om te laten controleren of het sterk is',
      ],
      goed: 1,
      uitleg:
        'Gereedschap vragen deelt geen gegevens. De andere drie bevatten '
        + 'persoonsgegevens of een geheim.',
    },
  ],
}
